import { ApolloClient, InMemoryCache, ApolloLink } from "@apollo/client";
import { HttpLink } from "@apollo/client/link/http";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_SERVER_URL as string,
  credentials: "include",
});

const wsUrl = (process.env.REACT_APP_SERVER_WS_URL || "").trim();
const hasValidWsUrl = wsUrl.startsWith("ws://") || wsUrl.startsWith("wss://");

const wsLink = hasValidWsUrl
  ? new GraphQLWsLink(
      createClient({
        url: wsUrl,
        retryAttempts: 5,
      })
    )
  : null;

const supportEscalationCodes = new Set([
  "VERIFICATION_FAILED",
  "OFFER_EXPIRED",
  "NO_REDEMPTION_CODES",
  "NO_PARTNER_VOUCHER_CODES",
  "USAGE_LIMIT_REACHED",
  "USER_WALLET_NOT_FOUND",
  "RECIPIENT_WALLET_NOT_FOUND",
  "INSUFFICIENT_FUNDS",
  "BAD_USER_INPUT",
  "SERVICE_UNAVAILABLE",
]);

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (typeof window === "undefined") {
    return;
  }

  const firstGraphQLError = graphQLErrors?.[0];
  const errorCode = firstGraphQLError?.extensions?.code;

  if (typeof errorCode === "string" && supportEscalationCodes.has(errorCode)) {
    const supportRef = firstGraphQLError?.extensions?.["supportRef"];

    window.dispatchEvent(
      new CustomEvent("njoy:support-error", {
        detail: {
          operationName: operation.operationName || "unknown",
          route: window.location.pathname,
          errorCode,
          supportRef: typeof supportRef === "string" ? supportRef : undefined,
          message: firstGraphQLError?.message ?? "GraphQL error",
        },
      })
    );
  }

  if (networkError) {
    window.dispatchEvent(
      new CustomEvent("njoy:support-error", {
        detail: {
          operationName: operation.operationName || "unknown",
          route: window.location.pathname,
          errorCode: "NETWORK_ERROR",
          message: networkError.message,
        },
      })
    );
  }
});

const splitLink = wsLink
  ? ApolloLink.split(
      ({ query }) => {
        const definition = getMainDefinition(query);

        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      wsLink,
      httpLink
    )
  : httpLink;

const link = ApolloLink.from([errorLink, splitLink]);

export const client = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});
